import React,{ useEffect,useState, useMemo } from 'react';
import { useNavigate,useParams } from 'react-router-dom';
import { ArrowLeft, Settings } from 'lucide-react';
import ActionEditor from './ActionEditor';
import ConditionEditor from './ConditionEditor';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';

const AutomationEditor = () => {
  const { automationId } = useParams();
  const navigate = useNavigate();
  const { apiRequest } = useAuth();
  
  
  const [automation, setAutomation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [devices, setDevices] = useState([]);
  const [scenes, setScenes] = useState([]);
  
  // For editing basic info
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);
  const [conditionLogic, setConditionLogic] = useState('AND');
  
  // Flags for editing mode
  const [isAddingCondition, setIsAddingCondition] = useState(false);
  const [isAddingAction, setIsAddingAction] = useState(false);
  const [editingConditionIndex, setEditingConditionIndex] = useState(null);
  const [editingActionIndex, setEditingActionIndex] = useState(null);

  // Stabilize devices and scenes references to avoid unnecessary re-renders from WebSocket updates
  const stableDevices = useMemo(() => devices, [devices]);
  const stableScenes = useMemo(() => scenes, [scenes]);

const API_BASE_URL = '';
const API_PATH = '/api';
  
  // Load automation data when component mounts
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load automation details
        const {response, data} = await apiRequest(`${API_BASE_URL}${API_PATH}/automations/${automationId}`);
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        const automationData = data;
        
        setAutomation(automationData);
        setName(automationData.name);
        setDescription(automationData.description || '');
        setActive(automationData.active);
        setConditionLogic(automationData.conditionLogic || 'AND');
        
        // Load devices and scenes for the editors
        const [devicesResult, scenesResult] = await Promise.all([
          apiRequest(`${API_BASE_URL}${API_PATH}/devices`),
          apiRequest(`${API_BASE_URL}${API_PATH}/scenes`)
        ]);
        
        const { response: devicesResponse, data: devicesData } = devicesResult;
        const { response: scenesResponse, data: scenesData } = scenesResult;
        
        if (!devicesResponse.ok || !scenesResponse.ok) {
          throw new Error('Failed to load devices or scenes');
        }
        
        // const devicesData = await devicesResponse.json();
        // const scenesData = await scenesResponse.json();
        
        setDevices(devicesData);
        setScenes(scenesData);
        
        setError(null);
      } catch (err) {
        console.error('Error loading automation data:', err);
        setError('Failed to load automation data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [automationId,apiRequest]);

  // Save basic automation info
  const handleSaveBasicInfo = async () => {
    try {
      const {response} = await apiRequest(`${API_BASE_URL}${API_PATH}/automations/${automationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          description,
          active,
          conditionLogic
        }),
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      // Update local state
      setAutomation(prev => ({
        ...prev,
        name,
        description,
        active,
        conditionLogic
      }));
    } catch (err) {
      console.error('Error saving automation:', err);
      setError('Failed to save automation');
    }
  };

  // Add a new condition
  const handleAddCondition = async (condition) => {
    try {
      const {response} = await apiRequest(`${API_BASE_URL}${API_PATH}/automations/${automationId}/add-condition`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ condition }),
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      // Optimistically update local state instead of refetching
      setAutomation(prev => ({
        ...prev,
        conditions: [...(prev.conditions || []), condition]
      }));
      
      // Exit adding mode
      setIsAddingCondition(false);
    } catch (err) {
      console.error('Error adding condition:', err);
      setError('Failed to add condition');
    }
  };

  // Update an existing condition
  const handleUpdateCondition = async (condition, index) => {
    try {
      const {response} = await apiRequest(`${API_BASE_URL}${API_PATH}/automations/${automationId}/conditions/${index}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ condition }),
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      // Optimistically update local state instead of refetching
      setAutomation(prev => ({
        ...prev,
        conditions: prev.conditions.map((c, i) => i === index ? condition : c)
      }));
      
      // Exit editing mode
      setEditingConditionIndex(null);
    } catch (err) {
      console.error('Error updating condition:', err);
      setError('Failed to update condition');
    }
  };

  // Delete a condition
  const handleDeleteCondition = async (index) => {
    try {
      const {response} = await apiRequest(`${API_BASE_URL}${API_PATH}/automations/${automationId}/conditions/${index}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      // Optimistically update local state instead of refetching
      setAutomation(prev => ({
        ...prev,
        conditions: prev.conditions.filter((_, i) => i !== index)
      }));
    } catch (err) {
      console.error('Error deleting condition:', err);
      setError('Failed to delete condition');
    }
  };

  // Add a new action
  const handleAddAction = async (action) => {
    try {
      const {response} = await apiRequest(`${API_BASE_URL}${API_PATH}/automations/${automationId}/add-action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      // Optimistically update local state instead of refetching
      setAutomation(prev => ({
        ...prev,
        actions: [...(prev.actions || []), action]
      }));

      // Exit adding mode
      setIsAddingAction(false);
    } catch (err) {
      console.error('Error adding action:', err);
      setError('Failed to add action');
    }
  };

  // Update an existing action
  const handleUpdateAction = async (action, index) => {
    try {
      const {response} = await apiRequest(`${API_BASE_URL}${API_PATH}/automations/${automationId}/actions/${index}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action }),
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      // Optimistically update local state instead of refetching
      setAutomation(prev => ({
        ...prev,
        actions: prev.actions.map((a, i) => i === index ? action : a)
      }));
      
      // Exit editing mode
      setEditingActionIndex(null);
    } catch (err) {
      console.error('Error updating action:', err);
      setError('Failed to update action');
    }
  };

  // Delete an action
  const handleDeleteAction = async (index) => {
    try {
      const {response} = await apiRequest(`${API_BASE_URL}${API_PATH}/automations/${automationId}/actions/${index}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      // Optimistically update local state instead of refetching
      setAutomation(prev => ({
        ...prev,
        actions: prev.actions.filter((_, i) => i !== index)
      }));
    } catch (err) {
      console.error('Error deleting action:', err);
      setError('Failed to delete action');
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading automation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-destructive mb-4">{error}</p>
          <Button variant="outline" onClick={() => navigate('/automations')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Automations
          </Button>
        </div>
      </div>
    );
  }

  if (!automation) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Automation not found</p>
          <Button variant="outline" onClick={() => navigate('/automations')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Automations
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      <div className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="flex items-center gap-3 px-4 py-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/automations')}
            className="h-8 px-2"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-lg font-semibold tracking-tight">Edit Automation</h1>
        </div>
      </div>

      <div className="p-4 space-y-6">

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium">Name</Label>
            <Input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-10"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-medium">Description</Label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          <div className="flex items-center justify-between pt-2">
            <div className="space-y-1">
              <Label htmlFor="active" className="text-sm font-medium">Active</Label>
              <p className="text-xs text-muted-foreground">
                Enable or disable this automation
              </p>
            </div>
            <Switch
              id="active"
              checked={active}
              onCheckedChange={setActive}
            />
          </div>

          <Button onClick={handleSaveBasicInfo} className="w-full h-10">
            Save Changes
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Metadata</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Created:</span>
              <span className="ml-2 font-medium">{formatDate(automation.created)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Last Updated:</span>
              <span className="ml-2 font-medium">{formatDate(automation.lastUpdated)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Last Triggered:</span>
              <span className="ml-2 font-medium">{formatDate(automation.lastTriggered)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-base">When</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              {automation.conditions.length > 1 && (
                <div className="flex items-center gap-2 min-w-0">
                  <Label htmlFor="conditionLogic" className="text-sm text-muted-foreground shrink-0">Logic:</Label>
                  <select
                    id="conditionLogic"
                    value={conditionLogic}
                    onChange={(e) => setConditionLogic(e.target.value)}
                    className="h-9 min-w-0 flex-1 rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="AND">ALL conditions (AND)</option>
                    <option value="OR">ANY condition (OR)</option>
                  </select>
                </div>
              )}
              <Button
                variant="outline"
                onClick={() => setIsAddingCondition(true)}
                className="h-9 gap-2 shrink-0"
              >
                <Settings className="h-4 w-4" />
                Add Condition
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {automation.conditions.length === 0 && !isAddingCondition ? (
            <div className="rounded-lg border border-dashed border-border p-8 text-center bg-muted/20">
              <p className="text-sm text-muted-foreground">
                No conditions set. Add conditions to specify when this automation should trigger.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {automation.conditions.map((condition, index) => (
                <React.Fragment key={index}>
                  {editingConditionIndex === index ? (
                    <ConditionEditor
                      condition={condition}
                      devices={stableDevices}
                      onSave={(updatedCondition) => handleUpdateCondition(updatedCondition, index)}
                      onCancel={() => setEditingConditionIndex(null)}
                    />
                  ) : (
                    <div className="rounded-md border border-border p-4 bg-muted/20 hover:bg-muted/30 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 text-sm">
                          {condition.type === 'device' && (
                            <span>
                              <span className="text-muted-foreground">When</span>{' '}
                              <b>{stableDevices.find(d => d.id === condition.deviceId)?.name || condition.deviceId}</b>{' '}
                              <b>{condition.attribute}</b> {condition.operator} <b>{condition.value}</b>
                            </span>
                          )}
                          {condition.type === 'time' && (
                            <span>
                              <span className="text-muted-foreground">When</span>{' '}
                              time {condition.operator === 'between'
                                ? `between ${condition.start} and ${condition.end}`
                                : `is ${condition.value}`}
                            </span>
                          )}
                          {condition.type === 'day' && (
                            <span>
                              <span className="text-muted-foreground">When</span>{' '}
                              day {condition.operator === 'in'
                                ? `is one of [${condition.value.join(', ')}]`
                                : `is ${condition.value}`}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingConditionIndex(index)}
                            className="h-8 px-3 text-xs"
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteCondition(index)}
                            className="h-8 px-3 text-xs text-destructive hover:text-destructive"
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  {index < automation.conditions.length - 1 && (
                    <div className="flex items-center justify-center py-1">
                      <span className="px-2 py-0.5 rounded-full bg-muted text-xs font-medium">
                        {conditionLogic}
                      </span>
                    </div>
                  )}
                </React.Fragment>
              ))}

              {isAddingCondition && (
                <div className="rounded-md border border-border p-4 bg-muted/20">
                  <ConditionEditor
                    devices={stableDevices}
                    onSave={handleAddCondition}
                    onCancel={() => setIsAddingCondition(false)}
                  />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Then</CardTitle>
            <Button
              variant="outline"
              onClick={() => setIsAddingAction(true)}
              className="h-9 gap-2"
            >
              <Settings className="h-4 w-4" />
              Add Action
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {automation.actions.length === 0 && !isAddingAction ? (
            <div className="rounded-lg border border-dashed border-border p-8 text-center bg-muted/20">
              <p className="text-sm text-muted-foreground">
                No actions defined. Add actions to specify what should happen when this automation triggers.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {automation.actions.map((action, index) => (
                <div key={index} className="rounded-md border border-border p-4 bg-muted/20 hover:bg-muted/30 transition-colors">
                  {editingActionIndex === index ? (
                    <ActionEditor
                      action={action}
                      devices={stableDevices}
                      scenes={stableScenes}
                      onSave={(updatedAction) => handleUpdateAction(updatedAction, index)}
                      onCancel={() => setEditingActionIndex(null)}
                    />
                  ) : (
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 text-sm">
                        {action.type === 'device' && (
                          <span>
                            <span className="text-muted-foreground">Then</span>{' '}
                            control <b>{stableDevices.find(d => d.id === action.device)?.name || action.device}</b>
                          </span>
                        )}
                        {action.type === 'scene' && (
                          <span>
                            <span className="text-muted-foreground">Then</span>{' '}
                            activate scene <b>{stableScenes.find(s => s.id === action.sceneId)?.name || action.sceneId}</b>
                          </span>
                        )}
                        {action.type === 'notification' && (
                          <span>
                            <span className="text-muted-foreground">Then</span>{' '}
                            show notification: <b>{action.message}</b>
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingActionIndex(index)}
                          className="h-8 px-3 text-xs"
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteAction(index)}
                          className="h-8 px-3 text-xs text-destructive hover:text-destructive"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {isAddingAction && (
                <div className="rounded-md border border-border p-4 bg-muted/20">
                  <ActionEditor
                    devices={stableDevices}
                    scenes={stableScenes}
                    onSave={handleAddAction}
                    onCancel={() => setIsAddingAction(false)}
                  />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default AutomationEditor;